// src/pages/LMS/Students/StudentImport.js
import React, { useState } from 'react';
import {
  Box, Typography, Paper, Button, Grid, Alert, LinearProgress,
  Card, CardContent, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, IconButton, Divider
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  ArrowBack as BackIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const StudentImport = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [error, setError] = useState(null);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setImporting(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      // The backend (Part 7) requires chatbot_id as a query param
      const response = await axiosInstance.post('/api/lms/students/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, 
        params : { chatbot_id: chatbotId }
      });
      
      // Backend returns: { success, message, imported_count, skipped_count, errors: [] }
      setImportResults(response.data);
      setFile(null);
    } catch (error) {
      console.error('Error importing students:', error);
      setError(error.response?.data?.message || 'Error importing students. Please check your file format.');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = `email,first_name,last_name,phone,department,job_title
john.doe@company.com,John,Doe,+1-555-0100,Engineering,Software Engineer
jane.smith@company.com,Jane,Smith,+1-555-0101,Marketing,Marketing Manager`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'student_import_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleReset = () => {
    setFile(null);
    setImportResults(null);
    setError(null);
  };

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/lms/students`)}>
            <BackIcon />
          </IconButton>
          <Box sx={{ ml: 2 }}>
            <Typography variant="h4" fontWeight={700}>
              Import Students
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bulk import students from CSV or Excel file
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
        >
          Download Template
        </Button>
      </Box>

      {/* Results Section */}
      {importResults && (
        <Paper sx={{ p: 4, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {/* Show success icon if at least one imported, otherwise warning */}
            {importResults.imported_count > 0 ? (
                 <SuccessIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            ) : (
                 <WarningIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
            )}
           
            <Typography variant="h5" gutterBottom fontWeight={700}>
              {importResults.message}
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: '#f0fdf4', border: '1px solid #86efac' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="success.main" fontWeight={700}>
                    {importResults.imported_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Successfully Imported
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: '#fffbeb', border: '1px solid #fcd34d' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="warning.main" fontWeight={700}>
                    {importResults.skipped_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Skipped (Duplicates)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: '#fef2f2', border: '1px solid #fca5a5' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="error.main" fontWeight={700}>
                    {importResults.errors ? importResults.errors.length : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Failed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Error List from Backend */}
          {importResults.errors && importResults.errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }} icon={<ErrorIcon />}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Import Errors:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {importResults.errors.map((errMsg, index) => (
                  <li key={index}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {/* Backend sends strings like "Row 2: Email ... already exists" */}
                      {errMsg}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              onClick={() => navigate(`/chatbot/${chatbotId}/lms/students`)}
              size="large"
              sx={{ mr: 2 }}
            >
              View Students
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              size="large"
            >
              Import More
            </Button>
          </Box>
        </Paper>
      )}

      {/* Global Error Message (e.g., File format wrong) */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Upload Section - Only show if no results yet */}
      {!importResults && (
        <>
          <Paper sx={{ p: 4, mb: 3, textAlign: 'center' }}>
            <UploadIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Upload Student Data File
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Supported formats: CSV, Excel (.xlsx, .xls)
            </Typography>
            
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
              size="large"
              sx={{ mt: 2 }}
            >
              Choose File
              <input
                type="file"
                hidden
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
              />
            </Button>

            {file && (
              <Alert severity="info" sx={{ mt: 3, maxWidth: 500, mx: 'auto' }}>
                <Typography variant="body2" fontWeight={600}>
                  File selected: {file.name}
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleImport}
                  disabled={importing}
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  {importing ? 'Importing...' : 'Import Students'}
                </Button>
              </Alert>
            )}

            {importing && (
              <Box sx={{ mt: 3, maxWidth: 500, mx: 'auto' }}>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Processing file...
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Sample File Format Section */}
          <Paper sx={{ p: 4, bgcolor: '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Box component="span" sx={{ mr: 1 }}>📄</Box>
              Sample File Format
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Your CSV/Excel file must include headers in the first row. 
              Only <strong>email</strong> and <strong>first_name</strong> are strictly required.
            </Typography>

            {/* Sample Table */}
            <TableContainer component={Paper} sx={{ mb: 3, maxWidth: 900, mx: 'auto', boxShadow: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#475569' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>email *</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>first_name *</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>last_name</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>phone</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>department</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>john@example.com</TableCell>
                    <TableCell>John</TableCell>
                    <TableCell>Doe</TableCell>
                    <TableCell>+1-555-0100</TableCell>
                    <TableCell>Engineering</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: '#f9fafb' }}>
                    <TableCell>jane@example.com</TableCell>
                    <TableCell>Jane</TableCell>
                    <TableCell>Smith</TableCell>
                    <TableCell>+1-555-0101</TableCell>
                    <TableCell>Marketing</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info" icon={false} sx={{ border: '1px solid #93c5fd' }}>
                   <Typography variant="subtitle2" color="primary.dark" fontWeight={700}>
                     Rules:
                   </Typography>
                   <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                     <li>Headers must match exactly (case-insensitive, e.g., <code>email</code>, <code>first_name</code>).</li>
                     <li>Duplicate emails will be skipped and counted in the results.</li>
                     <li>Maximum 50 errors will be displayed if bulk failure occurs.</li>
                   </ul>
                </Alert>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default StudentImport;